import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { styles, SdiArrow, TextLink } from '@sdir/sds';
import { routes } from '../Routes/constants';
import NavigationButtons from '../Molecules/NavigationButtons';

export interface ApplicationLayoutProps {
  heading: React.ReactElement | string;
  ingress?: React.ReactElement | string;
  children: any;
  exitLabel?: string;
  mainContentMaxWidth?: string;
  hideNavigation?: boolean;
  navBackLabel?: string;
  navNextLabel?: string;
  canNavBack?: boolean;
  canNavNext?: boolean;
  hideHeading?: boolean;
  timeline?: React.ReactElement | string | null;
  onExitClick?: () => void;
  onNavNextClick?: () => void;
  onNavBackClick?: () => void;
}

export const ApplicationLayout: React.FC<ApplicationLayoutProps> = ({
  heading,
  ingress,
  children,
  exitLabel,
  navBackLabel,
  navNextLabel,
  canNavNext,
  canNavBack,
  hideNavigation = false,
  hideHeading = false,
  mainContentMaxWidth,
  timeline,
  onExitClick,
  onNavBackClick,
  onNavNextClick,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const handleNavBackClick = useCallback(() => {
    if (onNavBackClick) {
      onNavBackClick();
    } else {
      navigate(-1);
    }
  }, [navigate, onNavBackClick]);

  const handleExitClick = useCallback(() => {
    if (onExitClick) {
      onExitClick();
    } else {
      navigate(routes.home);
    }
  }, [navigate, onExitClick]);

  return (
    <Container>
      {!hideHeading && (
        <>
          {exitLabel && <BackLink text={exitLabel} onClick={handleExitClick} />}
          <TopSection>
            <Headings>
              <H1>{heading}</H1>
              {ingress && <Ingress>{ingress}</Ingress>}
            </Headings>
          </TopSection>
        </>
      )}

      <ContentWrapper mainContentMaxWidth={mainContentMaxWidth}>
        <MainContent>
          {children}
          <NavArea>
            {!hideNavigation && (
              <NavigationButtons
                backButtonText={navBackLabel || intl.formatMessage({ id: 'general.form.back' })}
                nextButtonText={navNextLabel || intl.formatMessage({ id: 'general.form.next' })}
                backButtonOnClick={handleNavBackClick}
                nextButtonOnClick={onNavNextClick}
                backDisabled={!canNavBack}
                nextDisabled={!canNavNext}
              />
            )}
          </NavArea>
        </MainContent>
        {timeline && (
          <TimeLineWrapper>
            <TimeLine>{timeline}</TimeLine>
          </TimeLineWrapper>
        )}
      </ContentWrapper>
    </Container>
  );
};

const BackLink = ({ text, onClick }) => {
  return (
    <BackLinkWrapper>
      <TextLink
        text={text}
        click={onClick}
        textColor="#000000"
        iconColor="#ffffff"
        icon={
          <BackIcon>
            <SdiArrow color="#ffffff" direction="left" size="s" height="8px" />
          </BackIcon>
        }
      />
    </BackLinkWrapper>
  );
};

const BackLinkWrapper = styled.div`
  padding-bottom: 3rem;
`;

const BackIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  margin-right: 5px;
  background-color: #00b299;
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 70%;
  order: 1;
  @media screen and (max-width: ${styles.screen.medium}) {
    order: 2;
  }
`;

const TimeLineWrapper = styled.div`
  margin-left: 30px;
  order: 2;
  @media screen and (max-width: ${styles.screen.medium}) {
    order: 1;
  }
`;

const NavArea = styled.div``;

const Container = styled.div`
  margin: 3rem 0;
  width: 100%;
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const ContentWrapper = styled.div<{ mainContentMaxWidth?: string }>`
  margin-top: 3rem;
  display: flex;
  flex-direction: row;
  ${({ mainContentMaxWidth }) => mainContentMaxWidth && `max-width: ${mainContentMaxWidth};`}

  @media screen and (max-width: ${styles.screen.medium}) {
    flex-direction: column;
  }
`;

const H1 = styled.h1`
  font-size: 4rem;
  font-family: ${styles.fonts.stfMedium};
  font-weight: normal;
`;

const Ingress = styled.p``;

const TimeLine = styled.div``;

const Headings = styled.div``;
